import { useCallback, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CurrentVariantType, ISavedModifier } from '../_types'
import { ICartItem, IRedux } from '../_store/types'
import {
  DECREMENT,
  INCREMENT,
  REMOVE,
  SET_QUANTITY,
} from '../_store/cart/cart.slice'
import { getActiveModifiersSignature } from '../_utils/modifier/buildCompulsoryModifiers'
import useGTM from './useGTM'

export default function useCartProduct(
  productId: string,
  selectedModifiers?: ISavedModifier[] | null,
  selVariants?: CurrentVariantType,
  isDivisible?: boolean
) {
  const [quantity, setQuantity] = useState(1)

  const dispatch = useDispatch()
  const { sendGTM } = useGTM()

  const getConcatenatedIds = (
    items: { variant_id?: string; modifier_id?: string }[],
    key: 'variant_id' | 'modifier_id'
  ): string => {
    return items
      ?.map((item) => item[key])
      ?.sort()
      ?.join('_')
  }

  const onCheckProduct = useCallback(
    (productById?: ICartItem) => {
      if (!productById) return false

      const productVariants =
        selVariants && productById?.variants
          ? getConcatenatedIds(
              Object.values(productById.variants),
              'variant_id'
            )
          : ''

      let myVariants = ''
      if (selVariants) {
        const selVariantsArr = Object.values(selVariants).map((item) => ({
          variant_id: item?.variant_id,
        }))
        myVariants = getConcatenatedIds(selVariantsArr, 'variant_id')
      }

      const cartModifiers = productById.order_modifiers ?? []
      const modifiersMatch =
        Array.isArray(selectedModifiers) &&
        getActiveModifiersSignature(cartModifiers) ===
          getActiveModifiersSignature(selectedModifiers)

      const modifierCheckPassed =
        selectedModifiers === undefined
          ? true
          : selectedModifiers === null
            ? false
            : selectedModifiers.length === 0 || modifiersMatch

      return (
        (!selVariants || productVariants === myVariants) && modifierCheckPassed
      )
    },
    [selectedModifiers, selVariants]
  )

  const cartItems = useSelector((state: IRedux) => state.cart?.cart ?? [])

  const matchingItems = useMemo(
    () =>
      cartItems.filter(
        (item) =>
          String(item.product_id) === String(productId) &&
          onCheckProduct(item)
      ),
    [cartItems, productId, onCheckProduct]
  )

  const productInCart = useMemo(() => {
    if (matchingItems.length === 0) return undefined
    if (matchingItems.length === 1) return matchingItems[0]

    return {
      ...matchingItems[0],
      quantity: matchingItems.reduce((sum, item) => sum + item.quantity, 0),
    }
  }, [matchingItems])

  const onRemove = () => {
    if (productInCart) {
      sendGTM('remove_from_cart', {
        cart: [productInCart],
      })
      dispatch(REMOVE(productInCart?.key))
      setQuantity(1)
    }
  }

  const minQuantity = isDivisible ? 0.1 : 1

  const onIncrease = () => {
    if (productInCart?.key) {
      dispatch(
        SET_QUANTITY({
          key: productInCart.key,
          quantity: isDivisible
            ? Number((productInCart.quantity + 1).toFixed(2))
            : productInCart.quantity + 1,
        })
      )
    } else {
      setQuantity((prev) =>
        isDivisible ? Number((prev + 1).toFixed(2)) : prev + 1
      )
    }
  }

  const onDecrease = () => {
    if (productInCart?.key) {
      const nextQuantity = productInCart.quantity - 1
      if (nextQuantity >= minQuantity) {
        dispatch(
          SET_QUANTITY({
            key: productInCart.key,
            quantity: isDivisible
              ? Number(nextQuantity.toFixed(2))
              : nextQuantity,
          })
        )
      } else {
        onRemove()
      }
    } else if (quantity - 1 >= minQuantity) {
      setQuantity((prev) =>
        isDivisible ? Number((prev - 1).toFixed(2)) : prev - 1
      )
    } else {
      setQuantity(1)
    }
  }

  const onSetQuantity = (val: number) => {
    if (productInCart?.key) {
      dispatch(SET_QUANTITY({ key: productInCart.key, quantity: val }))
    } else {
      setQuantity(val)
    }
  }

  return {
    quantity: productInCart ? productInCart.quantity : quantity,
    onRemove,
    onIncrease,
    onDecrease,
    onSetQuantity,
    setQuantity,
    productInCart: productInCart || null,
  }
}
